import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { DownloadSimple, Printer, ListChecks } from "@phosphor-icons/react";
import { api, downloadFile, errorMessage, fmtDate, inr, STATUS_CLASS, STATUS_LABEL } from "@/lib/api";
import { adminBtn, adminBtnGhost, adminInput } from "./AdminLayout";

const FILTERS = ["", "paid", "packed", "dispatched", "delivered", "pending", "failed"];

export default function AdminOrders() {
  const qc = useQueryClient();
  const [status, setStatus] = useState("");
  const [exporting, setExporting] = useState(false);
  const [selected, setSelected] = useState([]);
  const [bulk, setBulk] = useState({ status: "packed", courier_code: "", courier: "", awbs: "", notify: true });
  const [busy, setBusy] = useState(false);
  const { data: orders = [], isLoading } = useQuery({ queryKey: ["admin-orders", status], queryFn: async () => (await api.get("/admin/orders", { params: status ? { status } : {} })).data });
  const { data: couriers = [] } = useQuery({ queryKey: ["admin-couriers"], queryFn: async () => (await api.get("/admin/couriers")).data });

  const selectable = useMemo(() => orders.filter((o) => o.status !== "pending" && o.status !== "failed"), [orders]);
  const toggle = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const allSelected = selectable.length > 0 && selected.length === selectable.length;

  const exportCsv = async () => {
    setExporting(true);
    try { await downloadFile(`/admin/orders/export.csv${status ? `?status=${status}` : ""}`, `orders-${status || "all"}.csv`); toast.success("CSV downloaded"); }
    catch (e) { toast.error(errorMessage(e, "Export failed")); } finally { setExporting(false); }
  };

  const applyBulk = async () => {
    const awbList = bulk.awbs.split("\n").map((x) => x.trim()).filter(Boolean);
    const tracking_numbers = {};
    selected.forEach((id, i) => { if (awbList[i]) tracking_numbers[id] = awbList[i]; });
    setBusy(true);
    try {
      const { data } = await api.post("/admin/orders/bulk-status", {
        order_ids: selected, status: bulk.status, courier: bulk.courier, courier_code: bulk.courier_code,
        tracking_numbers, notify_buyer: bulk.notify,
      });
      toast.success(`${data.updated.length} order(s) updated${data.skipped.length ? ` · ${data.skipped.length} skipped (missing tracking)` : ""}`);
      setSelected([]); setBulk((b) => ({ ...b, awbs: "" }));
      qc.invalidateQueries({ queryKey: ["admin-orders"] });
    } catch (e) { toast.error(errorMessage(e)); } finally { setBusy(false); }
  };

  return (
    <div data-testid="admin-orders-page">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
        <h1 className="text-2xl font-medium tracking-tight">Orders</h1>
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex gap-1.5 overflow-x-auto">
            {FILTERS.map((f) => (
              <button key={f} onClick={() => { setStatus(f); setSelected([]); }} className={`rounded-full px-3 py-1.5 text-xs font-semibold ${status === f ? "bg-foreground text-white" : "bg-muted hover:bg-secondary"}`} data-testid={`orders-filter-${f || "all"}`}>{f ? STATUS_LABEL[f] : "All"}</button>
            ))}
          </div>
          <button onClick={() => downloadFile("/admin/dispatch-sheet.pdf", "dispatch-sheet.pdf")} className={adminBtnGhost} data-testid="dispatch-sheet-button"><ListChecks /> Dispatch sheet</button>
          <button onClick={exportCsv} disabled={exporting || !orders.length} className={adminBtnGhost} data-testid="export-orders-csv-button"><DownloadSimple /> {exporting ? "Exporting…" : `Export CSV${status ? ` (${STATUS_LABEL[status]})` : ""}`}</button>
        </div>
      </div>

      {selected.length > 0 && (
        <div className="mb-4 rounded-lg border border-primary/30 bg-[#F6EFE9] p-4" data-testid="bulk-bar">
          <div className="flex flex-wrap items-end gap-3">
            <p className="text-sm font-semibold" data-testid="bulk-selected-count">{selected.length} selected</p>
            <label className="text-xs"><span className="mb-1 block text-muted-foreground">Mark as</span>
              <select value={bulk.status} onChange={(e) => setBulk({ ...bulk, status: e.target.value })} className={adminInput} data-testid="bulk-status-select">
                <option value="packed">Packed</option>
                <option value="dispatched">Dispatched</option>
                <option value="delivered">Delivered</option>
              </select>
            </label>
            <label className="text-xs"><span className="mb-1 block text-muted-foreground">Courier</span>
              <select value={bulk.courier_code} onChange={(e) => setBulk({ ...bulk, courier_code: e.target.value, courier: couriers.find((c) => c.code === e.target.value)?.name || "" })} className={adminInput} data-testid="bulk-courier-select">
                <option value="">Keep as is</option>
                {couriers.map((c) => <option key={c.code} value={c.code}>{c.name}</option>)}
              </select>
            </label>
            <label className="min-w-[220px] flex-1 text-xs"><span className="mb-1 block text-muted-foreground">Tracking numbers — one per line, in the order selected</span>
              <textarea rows={2} value={bulk.awbs} onChange={(e) => setBulk({ ...bulk, awbs: e.target.value })} className={`${adminInput} h-auto py-2 font-mono`} data-testid="bulk-awb-input" />
            </label>
            <label className="flex items-center gap-1.5 text-xs"><input type="checkbox" checked={bulk.notify} onChange={(e) => setBulk({ ...bulk, notify: e.target.checked })} data-testid="bulk-notify-toggle" /> Email buyers</label>
            <button onClick={applyBulk} disabled={busy} className={adminBtn} data-testid="bulk-apply-button">{busy ? "Updating…" : "Apply"}</button>
            <button onClick={() => downloadFile(`/admin/labels.pdf?order_ids=${selected.join(",")}`, "labels.pdf")} className={adminBtnGhost} data-testid="bulk-labels-button"><Printer /> Print labels</button>
          </div>
        </div>
      )}

      <div className="overflow-x-auto rounded-lg border border-border bg-white">
        <table className="w-full text-sm">
          <thead className="bg-muted/60 text-left text-xs uppercase tracking-wider text-muted-foreground">
            <tr>
              <th className="px-3 py-3"><input type="checkbox" checked={allSelected} onChange={(e) => setSelected(e.target.checked ? selectable.map((o) => o.order_id) : [])} data-testid="select-all-orders" /></th>
              <th className="px-4 py-3">Order</th><th className="px-4 py-3">Customer</th><th className="px-4 py-3">Items</th><th className="px-4 py-3">Type</th><th className="px-4 py-3">Status</th><th className="px-4 py-3">Tracking</th><th className="px-4 py-3 text-right">Total</th>
            </tr>
          </thead>
          <tbody>
            {isLoading && <tr><td colSpan={8} className="px-4 py-8 text-center text-muted-foreground">Loading…</td></tr>}
            {!isLoading && orders.length === 0 && <tr><td colSpan={8} className="px-4 py-8 text-center text-muted-foreground">No orders</td></tr>}
            {orders.map((o) => (
              <tr key={o.order_id} className="border-t border-border/60 hover:bg-muted/40" data-testid={`admin-order-row-${o.order_id}`}>
                <td className="px-3 py-3">
                  <input type="checkbox" disabled={o.status === "pending" || o.status === "failed"} checked={selected.includes(o.order_id)} onChange={() => toggle(o.order_id)} data-testid={`select-order-${o.order_id}`} />
                </td>
                <td className="px-4 py-3"><Link to={`/admin/orders/${o.order_id}`} className="font-mono text-xs font-semibold text-primary hover:underline" data-testid={`admin-order-link-${o.order_id}`}>{o.order_id}</Link><p className="text-xs text-muted-foreground">{fmtDate(o.created_at)}</p></td>
                <td className="px-4 py-3"><p className="font-medium">{o.customer_name}</p><p className="text-xs text-muted-foreground">{o.customer_email}</p></td>
                <td className="px-4 py-3 text-xs">{o.items.map((i) => `${i.title} (${i.format === "ebook" ? "PDF" : `×${i.qty}`})`).join(", ")}{o.items.some((i) => i.dedication) && <span className="ml-1 rounded bg-[#F6E7DF] px-1.5 py-0.5 text-[10px] font-semibold text-[#9A4426]" data-testid={`signed-badge-${o.order_id}`}>✎ signed</span>}</td>
                <td className="px-4 py-3 text-xs">{o.has_physical && <span className="mr-1 rounded bg-[#F6E7DF] px-1.5 py-0.5 text-[#9A4426]">Ship</span>}{o.has_ebook && <span className="rounded bg-[#EEF1E4] px-1.5 py-0.5 text-accent">Ebook</span>}</td>
                <td className="px-4 py-3"><span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${STATUS_CLASS[o.status]}`}>{STATUS_LABEL[o.status]}</span></td>
                <td className="px-4 py-3 font-mono text-xs">
                  {o.tracking_number ? (o.tracking_url ? <a href={o.tracking_url} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline" data-testid={`track-link-${o.order_id}`}>{o.tracking_number}</a> : o.tracking_number) : <span className="text-muted-foreground">—</span>}
                </td>
                <td className="px-4 py-3 text-right font-semibold">{inr(o.total)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
